import { ref, computed, onUnmounted, type Ref, type CSSProperties } from "vue";

export interface UseDraggableOptions {
  initialWidth?: number;
  initialHeight?: number;
  clampPadding?: number;
  excludedSelectors?: string[];
}

export interface UseDraggableReturn {
  modalRef: Ref<HTMLElement | null>;
  isDragging: Ref<boolean>;
  isInitialPosition: Ref<boolean>;
  position: Ref<{ x: number; y: number }>;
  style: Ref<CSSProperties>;
  startDrag: (event: MouseEvent | TouchEvent) => void;
  reset: () => void;
}

const DEFAULT_EXCLUDED = [
  "button",
  "a",
  "input",
  "textarea",
  "[contenteditable]",
  ".ql-toolbar",
  ".ql-editor",
];

export function useDraggable(options: UseDraggableOptions = {}): UseDraggableReturn {
  const {
    initialWidth = 480,
    initialHeight = 600,
    clampPadding = 10,
    excludedSelectors = DEFAULT_EXCLUDED,
  } = options;

  const modalRef = ref<HTMLElement | null>(null);
  const isDragging = ref(false);
  const isInitialPosition = ref(true);
  const position = ref({ x: 0, y: 0 });
  const dragStart = ref({ x: 0, y: 0 });

  const style = computed<CSSProperties>(() => {
    if (isInitialPosition.value) return {};
    return {
      position: "fixed",
      left: `${position.value.x}px`,
      top: `${position.value.y}px`,
      transform: "none",
      margin: "0",
    };
  });

  const getClientCoords = (event: MouseEvent | TouchEvent) => {
    if ("touches" in event) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
    return { x: event.clientX, y: event.clientY };
  };

  const startDrag = (event: MouseEvent | TouchEvent) => {
    const target = event.target as HTMLElement;
    if (excludedSelectors.some((sel) => target.closest(sel))) return;

    event.preventDefault();
    event.stopPropagation();
    isDragging.value = true;

    const { x: clientX, y: clientY } = getClientCoords(event);

    if (isInitialPosition.value) {
      const modal = modalRef.value;
      if (modal) {
        const rect = modal.getBoundingClientRect();
        position.value = { x: rect.left, y: rect.top };
      } else {
        position.value = {
          x: (window.innerWidth - initialWidth) / 2,
          y: (window.innerHeight - initialHeight) / 2,
        };
      }
      isInitialPosition.value = false;
    }

    dragStart.value = {
      x: clientX - position.value.x,
      y: clientY - position.value.y,
    };

    document.addEventListener("mousemove", handleDrag, { passive: false });
    document.addEventListener("mouseup", stopDrag);
    document.addEventListener("touchmove", handleDrag, { passive: false });
    document.addEventListener("touchend", stopDrag);
  };

  const handleDrag = (event: MouseEvent | TouchEvent) => {
    if (!isDragging.value) return;

    event.preventDefault();
    event.stopPropagation();

    const { x: clientX, y: clientY } = getClientCoords(event);

    let newX = clientX - dragStart.value.x;
    let newY = clientY - dragStart.value.y;

    const modal = modalRef.value;
    if (modal) {
      const rect = modal.getBoundingClientRect();
      newX = Math.max(clampPadding, Math.min(newX, window.innerWidth - rect.width - clampPadding));
      newY = Math.max(
        clampPadding,
        Math.min(newY, window.innerHeight - rect.height - clampPadding)
      );
    }

    position.value = { x: newX, y: newY };
  };

  const stopDrag = () => {
    isDragging.value = false;
    document.removeEventListener("mousemove", handleDrag);
    document.removeEventListener("mouseup", stopDrag);
    document.removeEventListener("touchmove", handleDrag);
    document.removeEventListener("touchend", stopDrag);
  };

  const reset = () => {
    isInitialPosition.value = true;
    position.value = { x: 0, y: 0 };
  };

  onUnmounted(() => {
    stopDrag();
  });

  return {
    modalRef,
    isDragging,
    isInitialPosition,
    position,
    style,
    startDrag,
    reset,
  };
}
